/**
 * Flags: the server's numeric ids, the lowercase codes the client works in, the names players type and read, and
 * the gate between the flags the client shows and the ones the server accepts. The ids are ab-protocol's table,
 * shared with the server and the other clients. The codes, the names and the server's list are generated from
 * flag-icons and that table by tools/texturepacker/flag-processor.ts. The server rejects a code outside its table,
 * so a flag it lacks goes out as the closest one it has, a territory or region as the state it belongs to and any
 * other as the UN flag, while the client keeps and shows the choice; the gate lifts by itself for each flag the
 * server gains, as the table below says.
 */
import { FLAGS_CODE_TO_ISO } from '@airmash/protocol';
import {
  FLAG_CODES,
  FLAG_NAMES,
  SERVER_FLAG_CODES,
  type ExtendedFlagCode,
  type FlagCode,
  type ServerFlagCode,
} from '../generated/flags';

/** The placeholder for a flag not known yet, shown until the server says, and never a choice. */
export const UNKNOWN_FLAG = 'xx' satisfies FlagCode;

/** A flag a player can choose: any the client can show but the placeholder. */
export type SelectableFlag = Exclude<FlagCode, typeof UNKNOWN_FLAG>;

/** A flag the client shows that the server lacks, the placeholder aside. */
type GatedFlag = Exclude<ExtendedFlagCode, typeof UNKNOWN_FLAG>;

/** The UN flag stands in for a flag the server lacks and no state's flag stands closer to. */
const UN_FLAG = 'un' satisfies ServerFlagCode;

const FLAG_CODE_SET: ReadonlySet<string> = new Set(FLAG_CODES);
const SERVER_FLAG_SET: ReadonlySet<string> = new Set(SERVER_FLAG_CODES);

function isFlagCode(value: string): value is FlagCode {
  return FLAG_CODE_SET.has(value);
}

/** Whether a string, a saved setting or the backend's country say, is a flag a player can choose. */
export function isSelectableFlag(value: string): value is SelectableFlag {
  return value !== UNKNOWN_FLAG && FLAG_CODE_SET.has(value);
}

function isServerFlag(code: FlagCode): code is ServerFlagCode {
  return SERVER_FLAG_SET.has(code);
}

/** The flag for each server id, from the protocol's table; the generator checks the client has them all. */
const FLAG_BY_ID = new Map<number, FlagCode>();
for (const [id, code] of Object.entries(FLAGS_CODE_TO_ISO)) {
  const lower = code.toLowerCase();
  if (isFlagCode(lower)) FLAG_BY_ID.set(Number(id), lower);
}

/**
 * The server flag sent for an extended one until the server has it: a territory or region takes the state it
 * belongs to. Keyed over the extended codes only, so a flag the server gains turns its line into a type error:
 * delete the line then, and the table with toServerFlag once the server accepts every flag.
 */
const CLOSEST_SERVER_FLAG: Readonly<Partial<Record<GatedFlag, ServerFlagCode>>> = {
  // The United Kingdom's nations, Crown Dependencies and Overseas Territories
  ai: 'gb',
  bm: 'gb',
  dg: 'gb',
  fk: 'gb',
  'gb-eng': 'gb',
  'gb-nir': 'gb',
  'gb-sct': 'gb',
  'gb-wls': 'gb',
  gg: 'gb',
  gi: 'gb',
  gs: 'gb',
  io: 'gb',
  je: 'gb',
  ky: 'gb',
  ms: 'gb',
  pn: 'gb',
  sh: 'gb',
  'sh-ac': 'gb',
  'sh-hl': 'gb',
  'sh-ta': 'gb',
  tc: 'gb',
  vg: 'gb',
  // France's overseas departments, collectivities and territories
  bl: 'fr',
  cp: 'fr',
  gf: 'fr',
  gp: 'fr',
  mf: 'fr',
  mq: 'fr',
  nc: 'fr',
  pf: 'fr',
  pm: 'fr',
  re: 'fr',
  tf: 'fr',
  wf: 'fr',
  yt: 'fr',
  // The United States' territories
  as: 'us',
  gu: 'us',
  mp: 'us',
  um: 'us',
  vi: 'us',
  // The Kingdom of the Netherlands' Caribbean countries and municipalities
  aw: 'nl',
  bq: 'nl',
  cw: 'nl',
  sx: 'nl',
  // Australia's external territories
  cc: 'au',
  cx: 'au',
  hm: 'au',
  nf: 'au',
  // Spain's regions and the Canary Islands
  'es-ct': 'es',
  'es-ga': 'es',
  'es-pv': 'es',
  ic: 'es',
  // New Zealand's associated states and territory
  ck: 'nz',
  nu: 'nz',
  tk: 'nz',
  // Denmark's and Norway's territories, and Finland's Åland
  fo: 'dk',
  gl: 'dk',
  bv: 'no',
  sj: 'no',
  ax: 'fi',
};

/** The server's flag for a choice: the flag itself when the server has it, else the closest it does. */
export function toServerFlag(code: SelectableFlag): ServerFlagCode {
  return isServerFlag(code) ? code : (CLOSEST_SERVER_FLAG[code] ?? UN_FLAG);
}

/** The flag a server id names, or the placeholder for an id outside the table. */
export function flagFromId(id: number): FlagCode {
  return FLAG_BY_ID.get(id) ?? UNKNOWN_FLAG;
}

/** The flag's display name. */
export function flagName(code: FlagCode): string {
  return FLAG_NAMES[code];
}

/** The flag's sprite in the game sheet. */
export function flagTextureName(code: FlagCode): `flag_${FlagCode}` {
  return `flag_${code}`;
}

/** The flag's SVG for the UI, which the flag processor copies to public/vanilla/ui/flags. */
export function flagSvgPath(code: FlagCode): string {
  return `/vanilla/ui/flags/${code}.svg`;
}

/** Names players type that are neither a code nor a display name: the original client's names for some flags, and
 *  what people call the place. */
const ALIASES: Readonly<Record<string, SelectableFlag>> = {
  america: 'us',
  usa: 'us',
  'united states': 'us',
  britain: 'gb',
  'great britain': 'gb',
  uk: 'gb',
  'european union': 'eu',
  'russian federation': 'ru',
  turkey: 'tr',
  macedonia: 'mk',
  macao: 'mo',
  dprk: 'kp',
  korea: 'kr',
  czech: 'cz',
  uae: 'ae',
  holland: 'nl',
  'ivory coast': 'ci',
  pirate: 'jolly',
};

/** Codes by lowercase display name, for typed names. */
const CODE_BY_NAME = new Map<string, SelectableFlag>();
for (const code of FLAG_CODES) {
  if (isSelectableFlag(code)) CODE_BY_NAME.set(FLAG_NAMES[code].toLowerCase(), code);
}

/** The flag an input names: a code, an alias or a display name, in any case; null for anything else. */
export function resolveFlag(input: string): SelectableFlag | null {
  const term = input.trim().toLowerCase();
  if (isSelectableFlag(term)) return term;
  return ALIASES[term] ?? CODE_BY_NAME.get(term) ?? null;
}

/** Every term the autocomplete can complete to, codes first as before, shortest first so the first hit wins. */
const ALL_TERMS: readonly string[] = [
  ...FLAG_CODES.filter(isSelectableFlag),
  ...CODE_BY_NAME.keys(),
  ...Object.keys(ALIASES),
].sort((a, b) => a.length - b.length);

/**
 * The autocomplete's best match for a prefix: the shortest term starting with it, else the shortest containing it,
 * given as the flag it resolves to. isPrefix says the match extends what was typed, so the rest can be ghosted in.
 */
export function findFlagByPrefix(prefix: string): { name: string; isPrefix: boolean } | null {
  if (prefix.length === 0) return null;
  const lower = prefix.toLowerCase();
  let contains: string | null = null;
  for (const term of ALL_TERMS) {
    if (term.startsWith(lower)) return matchFor(term, true);
    if (contains === null && term.includes(lower)) contains = term;
  }
  return contains === null ? null : matchFor(contains, false);
}

function matchFor(term: string, isPrefix: boolean): { name: string; isPrefix: boolean } {
  const resolved = resolveFlag(term);
  return resolved !== null && resolved !== term ? { name: resolved, isPrefix: false } : { name: term, isPrefix };
}

/** What the picker's search matches for a flag: its code, its name and the aliases that name it. */
export function flagSearchTerms(code: FlagCode): readonly string[] {
  const terms = [code, FLAG_NAMES[code]];
  for (const [alias, target] of Object.entries(ALIASES)) {
    if (target === code) terms.push(alias);
  }
  return terms;
}
